import { appPalette, componentPalette, logPalette, neutralPalette, semanticPalette } from '../../theme/palette';
import { TrackerDataType } from './constants';

// Keep the Query Adder palette API stable, but prefer shared Broadsight tokens
// so adders inherit the same core color system as the rest of the app.
export const queryAdderPalette = {
  common: {
    white: neutralPalette.white,
    title: neutralPalette.black,
    text: neutralPalette.black,
    muted: neutralPalette.black,
    helper: neutralPalette.textHelper,
    headerDescription: neutralPalette.textHelper,
    softGray: neutralPalette.surfacePage,
    inputBorder: componentPalette.input.border,
    cardBorder: componentPalette.card.border,
    divider: neutralPalette.borderSubtle,
    headerBorder: neutralPalette.borderMuted,
    accentBorder: logPalette.content.border,
    accentSurface: logPalette.content.surface,
    tooltipBorder: neutralPalette.borderStrong,
    tooltipSurface: neutralPalette.surfaceMuted,
    modeBorder: neutralPalette.borderStrong,
    modeHoverBorder: neutralPalette.borderMuted,
    tagSurface: componentPalette.chip.background,
    tagBorder: componentPalette.chip.border,
    tagDelete: neutralPalette.borderStrong,
    inactivePillSurface: neutralPalette.surfaceRaised,
    inactivePillBorder: neutralPalette.borderSubtle,
    inactivePillText: neutralPalette.black,
    successSurface: semanticPalette.success.fill,
    successText: semanticPalette.success.text,
    actionBorder: neutralPalette.borderSubtle,
    actionText: neutralPalette.black,
  },
  service: {
    main: logPalette.service.main,
    accent: logPalette.service.light,
    accentDark: logPalette.service.dark,
    tile: logPalette.service.surface,
    modeBorderStrong: logPalette.service.borderStrong,
    modeSurfaceStrong: logPalette.service.surfaceStrong,
    modeBorderSoft: logPalette.service.border,
    modeSurfaceSoft: logPalette.service.surface,
    actionPrimary: logPalette.service.main,
    actionBorder: logPalette.service.border,
    actionText: logPalette.service.text,
    uploadBorder: logPalette.service.border,
    uploadSurface: logPalette.service.surface,
    standaloneInfoBorder: logPalette.service.border,
    standaloneInfoSurface: logPalette.service.surface,
  },
  mediaInteraction: {
    main: logPalette.mediaInteraction.main,
    accent: logPalette.mediaInteraction.main,
    accentDark: logPalette.mediaInteraction.dark,
    tile: logPalette.mediaInteraction.surface,
    modeBorderStrong: logPalette.mediaInteraction.borderStrong,
    modeSurfaceStrong: logPalette.mediaInteraction.surfaceStrong,
    modeBorderSoft: logPalette.mediaInteraction.border,
    modeSurfaceSoft: logPalette.mediaInteraction.surface,
    actionPrimary: logPalette.mediaInteraction.main,
    actionBorder: logPalette.mediaInteraction.border,
    actionText: logPalette.mediaInteraction.text,
    uploadBorder: logPalette.mediaInteraction.border,
    uploadSurface: logPalette.mediaInteraction.surface,
    standaloneInfoBorder: logPalette.mediaInteraction.border,
    standaloneInfoSurface: logPalette.mediaInteraction.surface,
  },
  content: {
    main: logPalette.content.main,
    accent: '#2D99F4',
    accentDark: '#155BB5',
    tile: logPalette.content.surface,
    modeBorderStrong: logPalette.content.borderStrong,
    modeSurfaceStrong: logPalette.content.surfaceStrong,
    modeBorderSoft: logPalette.content.border,
    modeSurfaceSoft: logPalette.content.surface,
    actionPrimary: logPalette.content.main,
    actionBorder: logPalette.content.border,
    actionText: logPalette.content.text,
    uploadBorder: logPalette.content.border,
    uploadSurface: logPalette.content.surface,
    standaloneInfoBorder: logPalette.content.border,
    standaloneInfoSurface: logPalette.content.surface,
  },
  engagement: {
    main: logPalette.engagement.main,
    accentDark: logPalette.engagement.dark,
    tile: logPalette.engagement.surface,
    service: logPalette.service.main,
    serviceDark: logPalette.service.dark,
    serviceTile: logPalette.service.surface,
    modeBorder: logPalette.engagement.borderStrong,
    modeSurface: logPalette.engagement.surface,
    actionPrimary: logPalette.engagement.main,
    actionBorder: logPalette.engagement.border,
    actionText: logPalette.engagement.text,
    uploadBorder: logPalette.engagement.border,
    uploadSurface: logPalette.engagement.surface,
    standaloneInfoBorder: logPalette.engagement.border,
    tonePositiveText: '#216A35',
    tonePositiveMutedText: logPalette.engagement.text,
    toneNeutralText: neutralPalette.textPrimary,
    toneNeutralMutedText: neutralPalette.textSecondary,
    toneNegativeText: semanticPalette.error.text,
    toneNegativeMutedText: '#912018',
    toneNeutralDot: '#6B7280',
    toneNegativeDot: '#D92D20',
    tonePositiveSurface: logPalette.engagement.surface,
    tonePositiveBorder: logPalette.engagement.borderStrong,
    toneNeutralSurface: neutralPalette.surfaceRaised,
    toneNeutralBorder: neutralPalette.borderMuted,
    toneNeutralBorderMuted: neutralPalette.borderSubtle,
    toneNegativeSurface: semanticPalette.error.fill,
    toneNegativeBorder: '#F4C7C3',
    toneNegativeBorderMuted: '#F0D5D2',
  },
  status: {
    inProgress: appPalette.status.inProgress,
    completed: appPalette.status.completed,
    participation: appPalette.status.participation,
    cancelled: appPalette.status.cancelled,
  },
} as const;

const queryAdderPaletteByType: Record<string, any> = {
  [TrackerDataType.SERVICE]: queryAdderPalette.service,
  [TrackerDataType.MEDIA_INTERACTION]: queryAdderPalette.mediaInteraction,
  [TrackerDataType.ENGAGEMENT]: queryAdderPalette.engagement,
  [TrackerDataType.CONTENT_LOG]: queryAdderPalette.content,
};

export const getQueryAdderUiPalette = (type: TrackerDataType) => {
  return {
    ...queryAdderPalette.common,
    ...(queryAdderPaletteByType[type as string] ?? queryAdderPalette.service),
  };
};

export const queryAdderTypeScale = {
  eyebrow: '14px',
  fieldLabel: '14px',
  helperText: '12px',
  bodyText: '16px',
  sectionTitle: '18px',
  modalTitle: '28px',
  buttonText: '16px',
  chipText: '12px',
} as const;
